import { Button, IconButton, Stack, Tooltip, TooltipProps, Typography, tooltipClasses } from '@mui/material'
import { ReactElement } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import styled from '@emotion/styled'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import { OnboardingStep } from 'api/model'
import { COLORS } from 'app-constants'

const DarkTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: COLORS.GRAY_0,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    padding: 0,
    backgroundColor: COLORS.GRAY_0,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: '2px 2px 12px 0px rgba(0, 0, 0, 0.30)',
    fontSize: 11,
  },
}))

const FtuxTooltip = ({
  headerText,
  isLastStep,
  subtitleText,
  progressText,
  onBackClick,
  onNextClick,
  onDismiss,
  placement,
  children,
  open,
  setOpen,
  updateOnboardingStep,
}: {
  headerText: string
  isLastStep?: boolean
  subtitleText: string
  progressText?: string
  onBackClick?: () => void
  onNextClick?: () => void
  onDismiss: () => void
  placement: TooltipProps['placement']
  children: ReactElement<any, any>
  open: boolean
  setOpen: (open: boolean) => void
  updateOnboardingStep: () => void
}) => {
  return (
    <DarkTooltip
      open={open}
      arrow
      placement={placement}
      componentsProps={{
        tooltip: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
      title={
        <Stack
          p="14px"
          borderRadius="12px"
          width="300px"
          sx={{
            backgroundColor: COLORS.GRAY_0,
          }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography
              variant="pSO"
              sx={{
                color: COLORS.PURE_WHITE,
              }}>
              {headerText}
            </Typography>
            <IconButton
              onClick={() => {
                setOpen(false)
                onDismiss()
                // if user dismisses, we assume that they don't want to see the ftux tour again
                updateOnboardingStep()
              }}
              sx={{
                padding: 0,
              }}>
              <CloseIcon
                sx={{
                  color: COLORS.PURE_WHITE,
                  height: '16px',
                  width: '16px',
                }}
              />
            </IconButton>
          </Stack>
          <Typography
            variant="pXS"
            mt={1}
            sx={{
              color: COLORS.PURE_WHITE,
            }}>
            {subtitleText}
          </Typography>

          {(progressText || onNextClick || onBackClick) && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" mt={1}>
              {progressText && (
                <Typography
                  variant="h8.75"
                  sx={{
                    color: COLORS.PURE_WHITE,
                  }}>
                  {progressText}
                </Typography>
              )}

              {(onNextClick || onBackClick) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {onBackClick && (
                    <Button
                      size="small"
                      disabled={!onBackClick}
                      onClick={onBackClick}
                      sx={{
                        color: COLORS.PURE_WHITE,
                      }}>
                      Back
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      onNextClick?.()
                      if (isLastStep) {
                        setOpen(false)
                      }
                    }}
                    sx={{
                      width: 'fit-content',
                      background: COLORS.PURE_WHITE,
                      color: COLORS.GRAY_0,
                      '&:hover': {
                        backgroundColor: COLORS.GRAY_3,
                      },
                    }}>
                    {isLastStep ? 'Done' : 'Next'}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      }>
      {children}
    </DarkTooltip>
  )
}

export default withOnboardingStep(FtuxTooltip, OnboardingStep.twin_ftux_tour_completed)
